import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { ImportedApplication } from 'components/Forms/ApplicationsForm';
import Checked from 'images/icons/Checked.svg?react';
import CheckedBlue from 'images/icons/CheckedBlue.svg?react';
import Close from 'images/icons/Close.svg?react';
import Unchecked from 'images/icons/Unchecked.svg?react';
import UncheckedBlue from 'images/icons/UncheckedBlue.svg?react';
import { useTheme } from 'styled-components';
import normalizeText from 'utils/normalizeText';

import {
    SActionButtonContainer,
    SApplicationListContainer,
    SApplicationListElement,
    SApplicationListElementCheckBoxContainer,
    SApplicationListElementCompany,
    SApplicationListElementContentContainer,
    SApplicationListElementJob,
    SApplicationListElementText,
    SApplicationListFooterContainer,
    SApplicationListSearch,
    SApplicationListSearchClearContainer,
    SApplicationListSearchContainer,
    SApplicationListSelectButton,
    SApplicationListSelectButtonContainer,
    SApplicationListSelectButtonContentContainer,
    SImportEmptyInfo,
} from './styles';

interface ApplicationSelectorProps {
    cancel: () => void;
    selectableApplications: ImportedApplication[];
    submit: (selectedApplications: ImportedApplication[]) => void;
}

const ApplicationSelector: FC<ApplicationSelectorProps> = ({ cancel, selectableApplications, submit }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
    const [search, setSearch] = useState<string>('');

    const handleClick = useCallback(
        (index: number) => {
            if (selectedIndexes.includes(index)) {
                setSelectedIndexes(selectedIndexes.filter((i) => i !== index));
            } else {
                setSelectedIndexes([...selectedIndexes, index]);
            }
        },
        [selectedIndexes],
    );

    const displayedIndexes = useMemo(() => {
        if (search) {
            const indexList: number[] = [];
            selectableApplications.forEach((app, index) => {
                if (normalizeText(`${app.company} ${app.job} ${app.place || ''}`).includes(normalizeText(search))) {
                    indexList.push(index);
                }
            });
            return indexList;
        }
        return Array.from({ length: selectableApplications.length }, (_, i) => i);
    }, [search, selectableApplications]);

    const isSelectAllMode = useMemo(() => {
        for (const value of displayedIndexes) {
            if (!selectedIndexes.includes(value)) return true;
        }
        return false;
    }, [displayedIndexes, selectedIndexes]);

    const handleSelectButtonClick = useCallback(() => {
        if (isSelectAllMode) {
            const toAdd = displayedIndexes.filter((value) => !selectedIndexes.includes(value));
            setSelectedIndexes((arr) => [...arr, ...toAdd]);
        } else {
            setSelectedIndexes(selectedIndexes.filter((value) => !displayedIndexes.includes(value)));
        }
    }, [displayedIndexes, isSelectAllMode, selectedIndexes]);

    const onSubmit = useCallback(() => {
        submit(selectedIndexes.map((i) => selectableApplications[i]));
    }, [selectedIndexes, selectableApplications, submit]);

    return (
        <div>
            {selectableApplications.length > 0 ? (
                <>
                    <SApplicationListSearchContainer>
                        <SApplicationListSearch
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={t('application.import.search-placeholder')}
                            value={search}
                        />
                        <SApplicationListSearchClearContainer>
                            <Button onClick={() => setSearch('')} type="button" variant="text">
                                <Close fill={theme.color.text.light} />
                            </Button>
                        </SApplicationListSearchClearContainer>
                    </SApplicationListSearchContainer>
                    <SApplicationListSelectButtonContainer>
                        <SApplicationListSelectButton
                            disabled={displayedIndexes.length < 1}
                            onClick={handleSelectButtonClick}
                            variant="text"
                        >
                            <SApplicationListSelectButtonContentContainer>
                                {isSelectAllMode ? (
                                    <>
                                        <UncheckedBlue />
                                        {t('contact.import.select-all')}
                                    </>
                                ) : (
                                    <>
                                        <CheckedBlue />
                                        {t('contact.import.unselect-all')}
                                    </>
                                )}
                            </SApplicationListSelectButtonContentContainer>
                        </SApplicationListSelectButton>
                        {t('application.import.selected-count', { count: selectedIndexes.length })}
                    </SApplicationListSelectButtonContainer>
                    {displayedIndexes.length > 0 ? (
                        <SApplicationListContainer>
                            {displayedIndexes.map((displayedIndex) => (
                                <SApplicationListElement
                                    key={`${displayedIndex}-${selectableApplications[displayedIndex].company}-${selectableApplications[displayedIndex].job}`}
                                    onClick={() => handleClick(displayedIndex)}
                                >
                                    <SApplicationListElementCheckBoxContainer>
                                        {selectedIndexes.includes(displayedIndex) ? <Checked /> : <Unchecked />}
                                    </SApplicationListElementCheckBoxContainer>
                                    <SApplicationListElementContentContainer>
                                        <SApplicationListElementJob>
                                            {selectableApplications[displayedIndex].job}
                                        </SApplicationListElementJob>
                                        <SApplicationListElementCompany>
                                            {selectableApplications[displayedIndex].company}
                                        </SApplicationListElementCompany>
                                        {selectableApplications[displayedIndex].place && (
                                            <SApplicationListElementText>
                                                {selectableApplications[displayedIndex].place}
                                            </SApplicationListElementText>
                                        )}
                                    </SApplicationListElementContentContainer>
                                </SApplicationListElement>
                            ))}
                        </SApplicationListContainer>
                    ) : (
                        <SImportEmptyInfo>{t('application.import.no-result-search')}</SImportEmptyInfo>
                    )}
                    <SApplicationListFooterContainer>
                        <SActionButtonContainer>
                            <Button onClick={() => cancel()} variant="text">
                                {t('common.cancel')}
                            </Button>
                            <Button disabled={selectedIndexes.length < 1} onClick={onSubmit}>
                                {t('application.import.label')}
                            </Button>
                        </SActionButtonContainer>
                    </SApplicationListFooterContainer>
                </>
            ) : (
                <SImportEmptyInfo>{t('application.import.no-application-imported')}</SImportEmptyInfo>
            )}
        </div>
    );
};

export default ApplicationSelector;
